import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useToken } from "../../../server/auth/use-token";
import { ImagesService } from "../../../server/cloudflare/images-service";
import { useDialog } from "../../../utils/hooks/use-dialog";
import { uuidv4 } from "../../../utils/uuid";
import { CeHelpButton } from "../../generic/components/ce-help-button";
import { FilePicker } from "../../generic/components/file-picker/file-picker";
import { CeCheckbox } from "../../generic/components/form-fields/ce-checkbox";
import { RemoveConfirmationDialog } from "./remove-confirmation-dialog";

import type { BrandKitLogo } from "../server/brand-kit-service";
import type { BrandKitManager } from "../types";
import type { FC } from "react";

interface BrandKitLogoPickerFormData {
  src: string;
  format: string;
  type: "logo" | "icon" | "symbol" | "other";
  theme: "primary" | "light" | "dark" | null;
  tag: string;
  is_default_logo: boolean;
}

export const BrandKitLogoPicker: FC<{
  open: boolean;
  handleClose: () => void;
  onChange: BrandKitManager["logo"]["add"];
  existingLogo?: BrandKitLogo;
  onEdit?: (logo: BrandKitLogo) => void;
  onDelete?: () => void;
}> = ({ open, handleClose, onChange, existingLogo, onEdit, onDelete }) => {
  const getToken = useToken();

  const methods = useForm<BrandKitLogoPickerFormData>({
    defaultValues: {
      src: existingLogo?.src ?? "",
      format: existingLogo?.format ?? "",
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      type: (existingLogo?.type as "logo" | "icon" | "symbol" | "other") ?? "logo",
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      theme: (existingLogo?.theme as "primary" | "light" | "dark" | null) ?? "primary",
      tag: existingLogo?.tag ?? "",
      is_default_logo: existingLogo?.is_default_logo ?? false,
    },
  });

  const { handleSubmit, control, setValue, watch, reset } = methods;

  const watchedType = watch("type");
  const watchedSrc = watch("src");

  // Reset form values when existingLogo changes
  useEffect(() => {
    if (existingLogo) {
      reset({
        src: existingLogo.src,
        format: existingLogo.format,
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        type: existingLogo.type as "logo" | "icon" | "symbol" | "other",
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        theme: existingLogo.theme as "primary" | "light" | "dark" | null,
        tag: existingLogo.tag ?? "",
        is_default_logo: existingLogo.is_default_logo ?? false,
      });
    } else {
      reset({
        src: "",
        format: "",
        type: "logo",
        theme: "primary",
        tag: "",
        is_default_logo: false,
      });
    }
  }, [existingLogo, reset]);

  const handleUpload = useCallback(
    async (file: File) => {
      const name = uuidv4();

      const imagesService = new ImagesService();
      const result = await imagesService.upload(file, name, await getToken());

      if (!result.success || !result.url) {
        toast.error("Something went wrong, please try again");
        return undefined;
      }

      const [_, format] = file.type.split("/");

      setValue("src", result.url);
      setValue("format", format!);
      return file;
    },
    [getToken, setValue],
  );

  const handleRemove = useCallback((): void => {
    setValue("src", "");
    setValue("format", "");
  }, [setValue]);

  const onSubmit = (data: BrandKitLogoPickerFormData): void => {
    if (data.src) {
      if (existingLogo && onEdit) {
        onEdit({ ...existingLogo, ...data });
      } else {
        onChange?.(data);
      }
      handleClose();
      reset();
    }
  };

  const handleDialogClose = (): void => {
    handleClose();
    reset();
  };

  const removeDialog = useDialog();
  const handleDelete = useCallback(() => {
    removeDialog.handleOpen();
    reset();
  }, [reset, removeDialog]);

  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <DialogTitle>{existingLogo ? "Edit Logo" : "Upload Logo"}</DialogTitle>
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit(onSubmit)(e);
          }}
        >
          <DialogContent>
            <Stack spacing={2}>
              <FilePicker
                value={watchedSrc}
                analyticsCategory="brandkit"
                handleUpload={handleUpload}
                accept={{ "image/*": [".png", ".jpeg", ".gif", ".jpg", ".svg"] }}
                text={{
                  add: "Add Image",
                  remove: "Remove Image",
                  uploadError: "Only one image can be uploaded at a time",
                }}
                handleRemove={handleRemove}
                extraButtons={
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      data-analytics-id="delete-logo"
                      sx={{ mt: 2 }}
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                    {onDelete && (
                      <RemoveConfirmationDialog
                        type="logo"
                        open={removeDialog.open}
                        onClose={removeDialog.handleClose}
                        onDelete={onDelete}
                      />
                    )}
                  </>
                }
              />
              <Stack direction="row" gap={1}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel htmlFor="brand-kit-logo-picker-type">Image format</InputLabel>
                      <Select
                        {...field}
                        inputProps={{
                          id: "brand-kit-logo-picker-type",
                          "aria-label": "Image format",
                        }}
                        label="Image format"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          // Reset theme when type changes to non-logo/symbol
                          if (!["logo", "symbol"].includes(e.target.value)) {
                            setValue("theme", null);
                            // oxlint-disable-next-line react/incompatible-library -- react-hook-form's watch() is not memoizable; nothing to fix on our side
                          } else if (watch("theme") === null) {
                            setValue("theme", "primary");
                          }
                        }}
                      >
                        <MenuItem value="logo">Logo</MenuItem>
                        <MenuItem value="icon">Icon</MenuItem>
                        <MenuItem value="symbol">Symbol</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                <CeHelpButton
                  variant="tooltip"
                  content={
                    <Stack p={2} gap={1} bgcolor="primary" maxWidth="300px">
                      <Typography variant="caption">Logo: Your primary company logo.</Typography>
                      <Typography variant="caption">
                        Symbol: Simplified graphic symbol of your brand, often without text.
                      </Typography>
                      <Typography variant="caption">
                        Icon: Small, simplified version of your logo or symbol, ideal for app icons,
                        favicons, LinkedIn or social media.
                      </Typography>
                      <Typography variant="caption">
                        Other: Anything else that doesn’t fit into the categories above.
                      </Typography>
                    </Stack>
                  }
                />
              </Stack>
              {["logo", "symbol"].includes(watchedType) && (
                <Stack direction="row" gap={1}>
                  <Controller
                    name="theme"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel htmlFor="brand-kit-logo-picker-theme">Image style</InputLabel>
                        <Select
                          {...field}
                          fullWidth
                          value={field.value ?? "primary"}
                          inputProps={{
                            id: "brand-kit-logo-picker-theme",
                            "aria-label": "Image style",
                          }}
                          label="Image style"
                        >
                          <MenuItem value="primary">Primary</MenuItem>
                          <MenuItem value="light">Light</MenuItem>
                          <MenuItem value="dark">Dark</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                  <CeHelpButton
                    variant="tooltip"
                    content={
                      <Stack p={2} gap={1} maxWidth="300px">
                        <Typography variant="caption">
                          Primary: Your primary full-color company image.
                        </Typography>
                        <Typography variant="caption">
                          Light: version for display on dark backgrounds.
                        </Typography>
                        <Typography variant="caption">
                          Dark: version for display on light or white backgrounds.
                        </Typography>
                      </Stack>
                    }
                  />
                </Stack>
              )}
              <Stack direction="row" gap={1}>
                <Controller
                  name="tag"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="brand-kit-logo-picker-tag"
                      label="Brand tag"
                      placeholder="An optional tag to categorise your logo"
                      fullWidth
                    />
                  )}
                />
                <CeHelpButton
                  variant="tooltip"
                  content={
                    <Stack p={2} gap={1} maxWidth="300px">
                      <Typography variant="caption">
                        Add a tag to help you find the logo when creating designs or formatting
                        emails
                      </Typography>
                    </Stack>
                  }
                />
              </Stack>
              <Box>
                <CeCheckbox<BrandKitLogoPickerFormData>
                  analyticsCategory="brandkit"
                  name="is_default_logo"
                  label="Make default"
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={1}>
              <Button
                data-analytics-id="brand-kit-logo-picker-cancel"
                variant="outlined"
                onClick={handleDialogClose}
              >
                Cancel
              </Button>
              <Button
                data-analytics-id="brand-kit-logo-picker-ok"
                variant="contained"
                type="submit"
                disabled={!watchedSrc}
              >
                OK
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
